import { FormEvent, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ListFilter, Plus, Tags, Trash2, Wand2 } from 'lucide-react';
import {
  AmRechnerBearbeiten,
  Button,
  Card,
  ConfirmDialog,
  DataTable,
  EmptyState,
  IconButton,
  NativeSelect,
  toast,
  useMobil,
  type Column,
} from '@elbklar/ui';

import { SeitenKopf } from '@/components/layout/SeitenKopf';
import { SelectField, TextField } from '@/components/Felder';
import { categoriesApi, categoryRulesApi } from '@/lib/api';
import { ganzzahl, hatFehler, hoechstens, ohne, pflicht, unlesbareFelder, zumErstenFehler, type Fehler } from '@/lib/pruefen';
import type { Category, CategoryRule } from '@/types';

const ARTEN = [
  { value: 'variabel', label: 'variabel' },
  { value: 'fix', label: 'fix' },
  { value: 'invest', label: 'Investition' },
  { value: 'einnahme', label: 'Einnahme' },
];

const FELDER = [
  { value: 'purpose', label: 'Verwendungszweck' },
  { value: 'counterparty', label: 'Empfänger' },
];

type Loeschziel = { art: 'kategorie'; eintrag: Category } | { art: 'regel'; eintrag: CategoryRule };

/**
 * Kategorien und Regeln — Ist-Daten (U3). Kategorien tragen alle Planposten
 * und Fixkosten; die Regeln ordnen Buchungen aus Import und Buchhaltung zu.
 * Schlicht und am Rechner; am Handy nur die Listen.
 */
export function KategorienPage() {
  const queryClient = useQueryClient();
  const mobil = useMobil();

  const [neueKategorie, setNeueKategorie] = useState({ name: '', art: 'variabel' });
  const [neueRegel, setNeueRegel] = useState({
    muster: '',
    feld: 'purpose',
    kategorie: '',
    prioritaet: '100',
  });
  const [kategorieFehler, setKategorieFehler] = useState<Fehler<'name'>>({});
  const [regelFehler, setRegelFehler] = useState<Fehler<'muster' | 'kategorie' | 'prioritaet'>>({});
  const [zuLoeschen, setZuLoeschen] = useState<Loeschziel | null>(null);

  const kategorien = useQuery({
    queryKey: ['categories'],
    queryFn: () => categoriesApi.list(),
  });

  const regeln = useQuery({
    queryKey: ['category-rules'],
    queryFn: categoryRulesApi.list,
  });

  const aktualisieren = () => {
    void queryClient.invalidateQueries({ queryKey: ['categories'] });
    void queryClient.invalidateQueries({ queryKey: ['category-rules'] });
    void queryClient.invalidateQueries({ queryKey: ['transactions'] });
    void queryClient.invalidateQueries({ queryKey: ['overview'] });
  };

  const kategorieAnlegen = useMutation({
    mutationFn: () =>
      categoriesApi.create({
        name: neueKategorie.name.trim(),
        kind: neueKategorie.art,
      }),
    onSuccess: () => {
      toast.success('Kategorie angelegt');
      setNeueKategorie({ name: '', art: 'variabel' });
      aktualisieren();
    },
    onError: () => toast.error('Name schon vergeben?'),
  });

  // Fehler beim Löschen erscheinen im Bestätigungsdialog, der dafür offen bleibt.
  const kategorieLoeschen = useMutation({
    mutationFn: (id: string) => categoriesApi.remove(id),
    onSuccess: () => {
      toast.info('Kategorie gelöscht, zugeordnete Einträge stehen jetzt ohne Kategorie');
      aktualisieren();
    },
  });

  const regelAnlegen = useMutation({
    mutationFn: () =>
      categoryRulesApi.create({
        pattern: neueRegel.muster.trim(),
        field: neueRegel.feld,
        category_id: neueRegel.kategorie,
        // Von `absenden` geprüft: eine ganze Zahl ab 0. Leer oder „-“ wurde
        // hier vorher still zu 100.
        priority: Number(neueRegel.prioritaet),
      }),
    onSuccess: () => {
      toast.success('Regel angelegt');
      setNeueRegel({ ...neueRegel, muster: '' });
      aktualisieren();
    },
    onError: () => toast.error('Regel konnte nicht angelegt werden'),
  });

  const regelLoeschen = useMutation({
    mutationFn: (id: string) => categoryRulesApi.remove(id),
    onSuccess: () => {
      toast.info('Regel gelöscht');
      aktualisieren();
    },
  });

  const regelnAnwenden = useMutation({
    mutationFn: categoryRulesApi.apply,
    onSuccess: (ergebnis) => {
      toast.success(`${ergebnis.zugeordnet} von ${ergebnis.geprueft} Buchungen zugeordnet, ${ergebnis.offen} bleiben offen`);
      aktualisieren();
    },
    onError: () => toast.error('Anwenden fehlgeschlagen'),
  });

  const kategorieName = (id: string) => kategorien.data?.find((k) => k.id === id)?.name ?? '–';

  const kategorieSpalten: Column<Category>[] = [
    { key: 'name', header: 'Name', strong: true, mobil: 'titel', render: (k) => k.name },
    { key: 'kind', header: 'Art', mobil: 'unter', render: (k) => ARTEN.find((a) => a.value === k.kind)?.label ?? k.kind },
    ...(mobil
      ? []
      : [
          {
            key: 'aktionen',
            header: '',
            align: 'end' as const,
            width: 56,
            render: (k: Category) => (
              <IconButton
                icon={Trash2}
                label="Löschen"
                variant="ghost"
                size="sm"
                onClick={() => setZuLoeschen({ art: 'kategorie', eintrag: k })}
              />
            ),
          },
        ]),
  ];

  const regelSpalten: Column<CategoryRule>[] = [
    {
      key: 'pattern',
      header: 'Regel',
      mobil: 'titel',
      render: (r) => (
        <span className="block max-w-[22rem] truncate">
          <span className="text-muted-foreground">{FELDER.find((f) => f.value === r.field)?.label} enthält</span>{' '}
          <strong className="font-medium">„{r.pattern}“</strong>
        </span>
      ),
    },
    { key: 'category_id', header: 'Kategorie', mobil: 'unter', render: (r) => kategorieName(r.category_id) },
    { key: 'priority', header: 'Priorität', align: 'end', mono: true, mobil: 'wert', render: (r) => r.priority },
    ...(mobil
      ? []
      : [
          {
            key: 'aktionen',
            header: '',
            align: 'end' as const,
            width: 56,
            render: (r: CategoryRule) => (
              <IconButton
                icon={Trash2}
                label="Löschen"
                variant="ghost"
                size="sm"
                onClick={() => setZuLoeschen({ art: 'regel', eintrag: r })}
              />
            ),
          },
        ]),
  ];

  return (
    <div className="space-y-6">
      <SeitenKopf
        title="Kategorien und Regeln"
        subtitle="Kategorien sortieren und werten aus — sie deckeln nichts. Regeln ordnen importierte Buchungen automatisch zu."
      />

      <AmRechnerBearbeiten className="md:hidden">Kategorien und Regeln anlegen und löschen geht am Rechner.</AmRechnerBearbeiten>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card title="Kategorien">
          {!mobil && (
            <form noValidate
              onSubmit={(e: FormEvent<HTMLFormElement>) => {
                e.preventDefault();
                // Grenzen aus CategoryCreate (finance-api).
                const neu = { name: pflicht(neueKategorie.name, 'Name fehlt') ?? hoechstens(neueKategorie.name, 120) };
                setKategorieFehler(neu);
                if (hatFehler(neu)) {
                  zumErstenFehler(e.currentTarget);
                  return;
                }
                kategorieAnlegen.mutate();
              }}
              className="mb-4 flex flex-wrap items-end gap-2"
            >
              <TextField
                label="Name"
                placeholder="Name der Kategorie"
                value={neueKategorie.name}
                onChange={(e) => {
                  setNeueKategorie({ ...neueKategorie, name: e.target.value });
                  setKategorieFehler((f) => ohne(f, 'name'));
                }}
                error={kategorieFehler.name}
                required
                className="min-w-40 flex-1"
              />
              <div className="w-36">
                <NativeSelect
                  aria-label="Art"
                  options={ARTEN}
                  value={neueKategorie.art}
                  onChange={(e) => setNeueKategorie({ ...neueKategorie, art: e.target.value })}
                />
              </div>
              <Button type="submit" icon={Plus} loading={kategorieAnlegen.isPending}>
                Kategorie anlegen
              </Button>
            </form>
          )}

          <DataTable
            columns={kategorieSpalten}
            rows={kategorien.data ?? []}
            loading={kategorien.isLoading}
            label="Kategorien"
            empty={<EmptyState icon={Tags} title="Noch keine Kategorien" description="Angelegte Kategorien erscheinen hier." />}
          />
        </Card>

        <Card
          title="Automatische Zuordnung"
          description="Erste passende Regel gewinnt (niedrigste Priorität zuerst). Bereits von Hand zugeordnete Buchungen werden nie überschrieben."
          actions={
            !mobil && (
              <Button size="sm" icon={Wand2} loading={regelnAnwenden.isPending} onClick={() => regelnAnwenden.mutate()}>
                Rückwirkend anwenden
              </Button>
            )
          }
        >
          {!mobil && (
            <form noValidate
              onSubmit={(e: FormEvent<HTMLFormElement>) => {
                e.preventDefault();
                // Grenzen aus CategoryRuleCreate (finance-api).
                const neu = {
                  muster: pflicht(neueRegel.muster, 'Suchwort fehlt') ?? hoechstens(neueRegel.muster, 255),
                  kategorie: pflicht(neueRegel.kategorie, 'Kategorie wählen'),
                  prioritaet: ganzzahl(neueRegel.prioritaet, {
                    min: 0,
                    pflicht: true,
                    unlesbar: unlesbareFelder(e.currentTarget).has('prioritaet'),
                  }),
                };
                setRegelFehler(neu);
                if (hatFehler(neu)) {
                  zumErstenFehler(e.currentTarget);
                  return;
                }
                regelAnlegen.mutate();
              }}
              className="mb-4 space-y-3"
            >
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <TextField
                  label="Enthält"
                  placeholder="Suchwort, z. B. aral"
                  value={neueRegel.muster}
                  onChange={(e) => {
                    setNeueRegel({ ...neueRegel, muster: e.target.value });
                    setRegelFehler((f) => ohne(f, 'muster'));
                  }}
                  error={regelFehler.muster}
                  required
                />
                <SelectField
                  label="In Feld"
                  options={FELDER}
                  value={neueRegel.feld}
                  onChange={(e) => setNeueRegel({ ...neueRegel, feld: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <SelectField
                  label="Kategorie"
                  placeholder="Kategorie wählen"
                  options={(kategorien.data ?? []).map((k) => ({ value: k.id, label: k.name }))}
                  value={neueRegel.kategorie}
                  onChange={(e) => {
                    setNeueRegel({ ...neueRegel, kategorie: e.target.value });
                    setRegelFehler((f) => ohne(f, 'kategorie'));
                  }}
                  error={regelFehler.kategorie}
                  required
                  className="sm:col-span-2"
                />
                <TextField
                  label="Priorität"
                  type="number"
                  name="prioritaet"
                  min={0}
                  value={neueRegel.prioritaet}
                  onChange={(e) => {
                    setNeueRegel({ ...neueRegel, prioritaet: e.target.value });
                    setRegelFehler((f) => ohne(f, 'prioritaet'));
                  }}
                  error={regelFehler.prioritaet}
                  required
                />
              </div>
              <Button type="submit" size="sm" icon={Plus} loading={regelAnlegen.isPending}>
                Regel anlegen
              </Button>
            </form>
          )}

          <DataTable
            columns={regelSpalten}
            rows={regeln.data ?? []}
            loading={regeln.isLoading}
            label="Regeln"
            empty={
              <EmptyState
                icon={ListFilter}
                title="Noch keine Regeln"
                description="Regeln erscheinen hier. Sie sparen nach jedem Import viel Handarbeit."
              />
            }
          />
        </Card>
      </div>

      <ConfirmDialog
        open={zuLoeschen !== null}
        onOpenChange={(offen) => !offen && setZuLoeschen(null)}
        title={zuLoeschen?.art === 'regel' ? 'Regel löschen' : 'Kategorie löschen'}
        description={
          zuLoeschen?.art === 'kategorie'
            ? `„${zuLoeschen.eintrag.name}“ wird gelöscht. Buchungen, Fixkosten und Planposten bleiben erhalten und stehen danach ohne Kategorie da; die Regeln dieser Kategorie entfallen.`
            : zuLoeschen?.art === 'regel'
              ? `Die Regel „${zuLoeschen.eintrag.pattern}“ wird gelöscht. Bereits zugeordnete Buchungen behalten ihre Kategorie.`
              : undefined
        }
        confirmLabel={zuLoeschen?.art === 'regel' ? 'Regel löschen' : 'Kategorie löschen'}
        tone="danger"
        onConfirm={() => {
          if (!zuLoeschen) return undefined;
          return zuLoeschen.art === 'kategorie'
            ? kategorieLoeschen.mutateAsync(zuLoeschen.eintrag.id)
            : regelLoeschen.mutateAsync(zuLoeschen.eintrag.id);
        }}
      />
    </div>
  );
}
